// Path: src/components/aols/privacy/OlsDataProvision.tsx
// Updated to fix the double footer issue by removing the local Footer and layout wrapper.
// Added a new section for Live Chat Privacy and Communication to enhance policy transparency.
// Provided complete file content as requested.
// 06/07/25 10:30 AM +07

import React, { useCallback, Fragment } from "react";
import styled from "styled-components";
import { Typography } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import logo from "/logo.jpg";
// REMOVED: Footer import is no longer needed in this component.
// REMOVED: Unused Layout import from 'antd'.
import { HomeOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

// Define props interface
interface OlsDataProvisionProps {}

// Styled components
// REMOVED: FullHeightLayout is no longer needed.

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important; /* Sunlight orange for active links */
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important; /* Sunlight yellow on hover */
  }

  .breadcrumb-separator {
    color: #fff; /* White separator for contrast */
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important; /* White for non-active items */
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px;
  background: #111;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  color: #fff;
  position: relative;
  margin-top: 40px;

  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
    margin-top: 30px;
  }
`;

const LogoImg = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 30px;
  cursor: pointer;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(255, 204, 0, 0.3);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 120px;
  }
`;

const MainTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

interface StyledLinkProps {
  $isButton?: boolean;
}

const MainTitleButton = styled(Link)<StyledLinkProps>`
  background: #ffaa00;
  color: #0427f4;
  font-size: 24px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: #ffcc00;
    color: #0427f4;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 6px 12px;
  }
`;

const ContactButton = styled(MainTitleButton)`
  font-size: 18px;
  padding: 6px 12px;

  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 4px 10px;
  }
`;

const LinkStyled = styled.a`
  color: #ffaa00;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ffcc00;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }
`;

const MainTitleText = styled(Title)`
  color: #ffffff !important;
  margin: 0 !important;
  font-size: 24px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const SectionTitle = styled(Title)`
  color: #ffffff !important;
  margin-top: 30px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const PolicyText = styled(Paragraph)`
  line-height: 1.8;
  color: #ddd;
  margin-bottom: 15px;
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const List = styled.ul`
  margin-left: 20px;
  list-style-type: disc;

  li {
    margin: 5px 0;
    color: #ddd;

    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
`;

const OlsDataProvision: React.FC<OlsDataProvisionProps> = () => {
  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    // UPDATED: Using React.Fragment instead of FullHeightLayout
    <Fragment>
      <Helmet>
        <title>OLS Data Provision Policy - OneLightSystem OLS</title>
        <meta
          name="description"
          content="Review the OLS Data Provision Policy for OneLightSystem OLS, detailing data management, privacy, preservation, and analytics for OLS users."
        />
        <meta
          name="keywords"
          content="OLS Data Provision, Privacy Policy, Data Management, OneLightSystem OLS, Data Preservation, Data Analytics, Blockchain Security"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLS Data Provision Policy - OneLightSystem OLS" />
        <meta
          property="og:description"
          content="Explore the OLS Data Provision Policy for OneLightSystem OLS, covering data management, privacy, preservation, and analytics for OLS users."
        />
        <meta property="og:url" content="https://www.olsme.com/OlsDataProvision" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLS Data Provision Policy - OneLightSystem OLS" />
        <meta
          name="twitter:description"
          content="Learn about the OLS Data Provision Policy for OneLightSystem OLS, including data management, privacy, preservation, and analytics."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <link rel="canonical" href="https://www.olsme.com/OlsDataProvision" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "OLS Data Provision Policy - OneLightSystem OLS",
            description:
              "OLS Data Provision Policy for OneLightSystem OLS, detailing data management, privacy, preservation, and analytics for OLS users.",
            url: "https://www.olsme.com/OlsDataProvision",
            publisher: {
              "@type": "Organization",
              name: "OneLightSystem OLS",
              logo: "https://www.olsme.com/logo.jpg",
              contactPoint: {
                "@type": "ContactPoint",
                email: "info@olsme.com",
                contactType: "Customer Service",
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "2033 Ralston Ave",
                addressLocality: "Belmont",
                addressRegion: "CA",
                postalCode: "94002",
                addressCountry: "US",
              },
              founder: {
                "@type": "Person",
                name: "Nazar Pankiv",
                jobTitle: "CEO",
              },
              sameAs: [
                "https://www.olsme.com",
                "https://www.ols-med.net",
                "https://twitter.com/onelightsystem",
                "https://www.facebook.com/onelightsystemols",
                "https://www.linkedin.com/company/one-light-system",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
              ],
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Main Privacy",
                  item: "https://www.olsme.com/privacy",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "OLS Data Provision",
                  item: "https://www.olsme.com/OlsDataProvision",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <CustomBreadcrumb>
          <span className="breadcrumb-item">
            <Link to="/" className="breadcrumb-link">
              <HomeOutlined /> Home
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item">
            <Link to="/privacy" className="breadcrumb-link">
              Main Privacy
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item breadcrumb-non-active">
            OLS Data Provision
          </span>
        </CustomBreadcrumb>
      </BreadcrumbWrapper>
      <ContentWrapper>
        <LinkStyled href="https://www.olsme.com" aria-label="Go to OneLightSystem OLS homepage">
          <LogoImg src={logo} alt="OneLightSystem OLS Logo" onClick={handleLogoClick} />
        </LinkStyled>
        <MainTitleWrapper>
          <MainTitleButton to="/privacy" aria-label="Go to Main Privacy page">
            onelightsystem
          </MainTitleButton>
          <MainTitleButton to="/OLSaeo" aria-label="Go to OLS iee.aeo page">
            OLS iee.aeo
          </MainTitleButton>
          <MainTitleText level={1}>OLS Data Provision Policy</MainTitleText>
        </MainTitleWrapper>

        <PolicyText>
          <strong>OneLightSystem OLS</strong>
          <br />
          OLS Data Provision Policy for OneLightSystem OLS
          <br />
          Version: Q2.3405
          <br />
          Last Updated: April 08, 2025
          <br />
          © OneLightSystem OLS, LLC | OLS iee.aeo
          <br />
          CEO: Nazar Pankiv
        </PolicyText>

        <SectionTitle level={2}>Introduction</SectionTitle>
        <PolicyText>
          Welcome to the OLS Data Provision Policy for OneLightSystem OLS (hereinafter referred to as "OLS"). This policy outlines the management, planning, and usage of data within the OLS ecosystem, including data submission, confidentiality, preservation, and analytics. OLS is committed to ensuring the privacy and security of user data while providing transparent data management practices.
        </PolicyText>

        <SectionTitle level={2}>1. Data Submission and Confidentiality</SectionTitle>
        <PolicyText>
          When submitting an OLS account type application (e.g., OLS Student, OLSME, OLSLM) or an OLS Intro Application:
        </PolicyText>
        <List>
          <li>
            <strong>Confidentiality:</strong> Applicant data remains confidential and will not be shared with any third parties. OLS ensures that personal data is securely stored and protected.
          </li>
          <li>
            <strong>Public Visibility:</strong> Confidential personal data can only be made visible in the OneLightSystem OLS Data Center by the user's explicit choice. Users may opt to display specific data (e.g., usernames or initials) in public portfolios, while sensitive information remains private.
          </li>
        </List>

        <SectionTitle level={2}>2. Data Mining and Submission Requirements</SectionTitle>
        <PolicyText>
          OLS users, including OLS Students, OLS Inuo, OLS Inu.ini, and OLS Eae, are required to periodically submit accurate data to the OLS Data Center as part of the data mining process:
        </PolicyText>
        <List>
          <li>
            <strong>Data Types:</strong> Users must submit data such as OLSLM (Light Minutes), OLSM (Meditation Sessions), and OLSME activity, as applicable.
          </li>
          <li>
            <strong>Frequency:</strong> Submissions are required optionally and periodically, as outlined in the OLS Intro Rules and Regulations.
          </li>
          <li>
            <strong>Confidentiality:</strong> All submitted data remains confidential for each OLS user. The value calibration of submitted data is determined by OLS Data Center algorithms, which are approved by OLS iee.aeo.
          </li>
        </List>

        <SectionTitle level={2}>3. OLS Data Policy Sharing</SectionTitle>
        <PolicyText>
          OLS enforces strict policies regarding the sharing of data and teachings:
        </PolicyText>
        <List>
          <li>
            <strong>OLS Student Sharing Restrictions:</strong> OLS Students may share visual data practices (e.g., pictures) but are prohibited from sharing teaching descriptions (via voice memos, videos, or verbally) unless they become an OLS Inuo. Violations may result in penalties equivalent to the OLS Intro value, as determined by OLS iee.aeo, or temporary/permanent removal from the OLS ecosystem.
          </li>
          <li>
            <strong>Prohibition on Teaching Sharing:</strong> OLS Students are strictly prohibited from sharing OLS data teachings®© in any form (description, voice memos, videos, or verbally) until they achieve OLS Inuo status.
          </li>
        </List>

        <SectionTitle level={2}>4. OLS Data Preservation</SectionTitle>
        <PolicyText>
          OLS ensures the long-term preservation of user data within the OLS Data Center:
        </PolicyText>
        <List>
          <li>
            <strong>Annual Archiving:</strong> All OLS data is archived annually, starting from the beginning of a new Light Year and concluding after approximately 365 days (4/90 Light Year cycle).
          </li>
          <li>
            <strong>Past User Data:</strong> Data for past users is archived for a term of 3 Light Years. After this period, users will receive an email notification offering the option to buy out their personal mined data or continue storing it under an agreement with OLS iee.aeo.
          </li>
        </List>

        <SectionTitle level={2}>5. OLS Data Analytics and Research</SectionTitle>
        <PolicyText>
          OLS conducts data analytics and research to improve its services and contribute to the development of the Light Civilization:
        </PolicyText>
        <List>
          <li>
            <strong>Analytics and Research:</strong> All OneLightSystem OLS data analytics, research, and statistics are conducted by OLS Iense and Aunselo departments under the oversight of OLS iee.aeo.
          </li>
          <li>
            <strong>Data Usage:</strong> Data collected for analytics and research purposes is anonymized to protect user privacy and is used solely to enhance OLS services and offerings.
          </li>
        </List>

        {/* ADDED: New section for Live Chat Privacy and Communication */}
        <SectionTitle level={2}>6. Live Chat Privacy and Communication</SectionTitle>
        <PolicyText>
          Our Live Support chat feature is designed to provide immediate and helpful assistance. All conversations are treated as confidential between the user and the OLS administration.
        </PolicyText>
        <List>
          <li>
            <strong>Ethical Conduct:</strong> OLS administrators are committed to upholding the highest standards of professionalism and respect. All support staff will follow <strong>moral and ethical common-sense guidance for respectful public communication</strong> at all times.
          </li>
          <li>
            <strong>Data Storage:</strong> Chat transcripts may be securely stored for quality assurance and to maintain a record of support interactions. This data is confidential and will not be shared with third parties, in accordance with our overarching privacy commitments.
          </li>
        </List>
        
        {/* UPDATED: Section numbers incremented */}
        <SectionTitle level={2}>7. Contact and Oversight</SectionTitle>
        <PolicyText>
          For questions or concerns regarding this policy or OLS data practices, please contact:
        </PolicyText>
        <List>
          <li>
            <strong>Authority:</strong> OLS iee.aeo oversees data management and policy updates.
          </li>
          <li>
            <strong>Contact:</strong> Reach out via{" "}
            <ContactButton to="/Contact" aria-label="Go to Contact page">
              Contact Page
            </ContactButton>.
          </li>
        </List>
      </ContentWrapper>
      {/* REMOVED: The <Footer /> component is no longer rendered here. */}
    </Fragment>
  );
};

export default OlsDataProvision;